import Spinner from 'components/Loading/Spinner'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  IntegrationServices,
  useMutation,
  useQuery,
  useRunInvalidationForMutation
} from '@sequencehq/api/utils'
import {
  Button,
  Skeleton,
  GreyGrey10,
  GreyGrey40,
  GreyGrey60,
  GreyGrey80,
  Primary13SemiBold,
  Primary13Regular,
  useNotifications,
  IconCircleInfo,
  Box,
  Flex
} from '@sequencehq/core-components'
import { getIntegrationName } from 'Integrations/utils/getIntegrationName'
import { ExistingEntityLink } from '../LinkEntityWidget/ExistingEntityLink'
import { UnlinkInvoiceModal } from './UnlinkInvoiceModal'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { ProtectedButton, usePermissions } from 'lib/rbac'
import { getSyncErrorDescription } from 'Integrations/integrationsConfig/common/shared'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { shouldShowPaymentStatusWarning } from './shouldShowPaymentStatusWarning'

interface LinkedInvoiceWidgetProps {
  invoiceId: string
  service: IntegrationServices
}

const useLinkedInvoiceWidget = (props: {
  service: IntegrationServices
  invoiceId: string
}) => {
  const serviceName = getIntegrationName(props.service)
  const { displayNotification } = useNotifications()
  const runMutationInvalidation = useRunInvalidationForMutation()
  const flags = useFeatureFlags()

  const invoiceQuery = useQuery(
    dashboardv99990101Client.getInvoice,
    { id: props.invoiceId },
    {
      select: invoice => {
        if (!invoice) {
          return null
        }

        const externalInvoice = invoice.linkedServices.find(
          ls => ls.externalService === props.service
        )

        return {
          customerId: invoice.customerId,
          statusAllowsLink: ['SENT', 'FINAL', 'VOIDED'].includes(
            invoice.status
          ),
          linkedInvoice: externalInvoice
            ? {
                id: externalInvoice.externalId,
                url: externalInvoice.externalUrl
              }
            : null,
          stripeEnabled: Boolean(invoice.paymentOptions?.includes('LINK'))
        }
      }
    }
  )

  const xeroPaymentSyncQuery = useQuery(
    dashboard20240730Client.getIntegration,
    'Xero',
    {
      enabled:
        props.service === 'Xero' && invoiceQuery.data?.stripeEnabled === true,
      select: integration =>
        integration?.service === 'Xero' &&
        integration.paymentSyncEnabled === true
    }
  )

  const customerQuery = useQuery(
    dashboardv99990101Client.getCustomer,
    { id: invoiceQuery.data?.customerId ?? '' },
    {
      enabled: Boolean(invoiceQuery.data?.customerId),
      select: customer => {
        if (!customer) {
          return null
        }

        const customerLink = customer.integrationIds.find(
          int => int.service === props.service
        )

        return customerLink
      }
    }
  )

  const linkingMutation = useMutation(
    dashboard20240730Client.postPushInvoiceToExternalService
  )

  const linkInvoice = () => {
    linkingMutation.mutate(
      {
        invoiceId: props.invoiceId,
        service: props.service
      },
      {
        onError: error => {
          displayNotification(`Failed to sync invoice to ${serviceName}`, {
            type: 'error',
            description: getSyncErrorDescription(error)
          })
        },
        onSuccess: () => {
          displayNotification(`Invoice synced to ${serviceName}`, {
            type: 'success'
          })
          /**
           * The external URL doesn't appear immediately, so wait a little while
           * and then bust the cache to ensure we load in the updated data.
           */
          setTimeout(() => {
            runMutationInvalidation(
              dashboard20240730Client.postPushInvoiceToExternalService
            )
          }, 2000)
        }
      }
    )
  }

  return {
    loading: invoiceQuery.isPending,
    serviceName,
    invoiceStatusAllowsLink: invoiceQuery.data?.statusAllowsLink,
    linkedInvoice: invoiceQuery.data?.linkedInvoice,
    link: {
      disabled: !customerQuery.data || linkingMutation.isPending,
      onClick: linkInvoice,
      linking: linkingMutation.isPending
    },
    showPaymentStatusWarning: shouldShowPaymentStatusWarning({
      service: props.service,
      isStripeEnabled: invoiceQuery.data?.stripeEnabled ?? false,
      flags,
      isXeroPaymentSyncEnabled: xeroPaymentSyncQuery.data ?? false
    })
  }
}

const NotReadyToLink = (props: {
  showPaymentStatusWarning: boolean
  serviceName: string
}) => (
  <Flex
    flexDirection="column"
    gap="4px"
    padding="12px"
    backgroundColor={
      props.showPaymentStatusWarning ? 'transparent' : GreyGrey10
    }
    borderRadius="8px"
    border={props.showPaymentStatusWarning ? `1px solid ${GreyGrey40}` : 'none'}
  >
    <Box {...Primary13SemiBold} color={GreyGrey80}>
      Invoice
    </Box>
    <Box
      {...Primary13Regular}
      color={GreyGrey60}
      data-testid="linkInvoiceWidget.willSyncWhenFinal"
    >
      This invoice will be synced once it's finalized in Sequence.
    </Box>
    {props.showPaymentStatusWarning && (
      <PaymentStatusWarning serviceName={props.serviceName} />
    )}
  </Flex>
)

const PaymentStatusWarning = (props: { serviceName: string }) => (
  <Box
    {...Primary13Regular}
    data-testid="linkInvoiceWidget.paymentStatusWarning"
    backgroundColor={GreyGrey10}
    borderRadius="8px"
    padding="8px"
  >
    <Flex gap="8px" alignItems="top">
      <Flex flex="10%">
        <IconCircleInfo />
      </Flex>
      <Flex direction="column">
        Payments collected via Stripe don't update the status of the invoice in{' '}
        {props.serviceName}.
      </Flex>
    </Flex>
  </Box>
)

export const LinkedInvoiceWidget = (props: LinkedInvoiceWidgetProps) => {
  const hook = useLinkedInvoiceWidget(props)
  const { can } = usePermissions()
  const canManageIntegrations = can('org.integration:update')

  if (hook.loading) {
    return <Skeleton height="104px" width="100%" />
  }

  if (!hook.invoiceStatusAllowsLink) {
    return (
      <NotReadyToLink
        showPaymentStatusWarning={hook.showPaymentStatusWarning}
        serviceName={hook.serviceName}
      />
    )
  }

  if (hook.linkedInvoice) {
    return (
      <ExistingEntityLink
        data-testid={`linkInvoiceWidget.${props.service}.linked`}
        externalEntityLabel="Invoice"
        serviceName={hook.serviceName}
        unlinkModal={({ onClose }) => (
          <UnlinkInvoiceModal
            service={props.service}
            invoiceId={props.invoiceId}
            onClose={onClose}
          />
        )}
        linkedEntity={{
          label: `ID: ${hook.linkedInvoice.id}`,
          href: hook.linkedInvoice.url,
          canUnlink: canManageIntegrations,
          isPending: false
        }}
        warnings={
          hook.showPaymentStatusWarning
            ? [<PaymentStatusWarning serviceName={hook.serviceName} />]
            : []
        }
      />
    )
  }

  return (
    <Flex
      flexDirection="column"
      gap="12px"
      padding="12px"
      backgroundColor={
        hook.showPaymentStatusWarning ? 'transparent' : GreyGrey10
      }
      borderRadius="8px"
      border={
        hook.showPaymentStatusWarning ? `1px solid ${GreyGrey40}` : 'none'
      }
      data-testid={`linkInvoiceWidget.${props.service}`}
    >
      <Flex direction="column" gap="4px">
        <Box {...Primary13SemiBold} color={GreyGrey80}>
          Invoice
        </Box>
        <Box {...Primary13Regular} color={GreyGrey60}>
          This invoice has not yet been synced to {hook.serviceName}.
        </Box>
      </Flex>
      <ProtectedButton permission={'org.invoice:sync'}>
        <Button
          variant="secondary"
          onClick={hook.link.onClick}
          disabled={hook.link.disabled}
          data-testid={`linkInvoiceWidget.${props.service}.syncInvoice`}
        >
          {hook.link.linking ? (
            <Flex gap="8px">
              <Spinner height="16px" width="16px" color={GreyGrey60} />
              Syncing...
            </Flex>
          ) : (
            'Sync invoice'
          )}
        </Button>
      </ProtectedButton>
      {hook.showPaymentStatusWarning && (
        <PaymentStatusWarning serviceName={hook.serviceName} />
      )}
    </Flex>
  )
}
